import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { QUADRANT_META } from '../../utils/structuralAnalysis';
import { useTranslation } from '../../hooks/useTranslation.jsx';

/**
 * Reach map — the structural "Klusterit" view for tree-shaped impact-path data.
 *
 * Why not MICMAC here: in a single-parent tree, influence (downstream reach) and
 * dependence (causal depth) are anti-correlated — high-reach nodes sit near the
 * root, deep nodes are leaves — so a MICMAC quadrant is an empty-cornered
 * anti-diagonal. A reach map fits the data: lanes by causal depth, bubbles sized
 * by downstream reach, coloured by quadrant. Big bubble = high-leverage
 * consequence. Leaves (reach 0) are hidden to show the load-bearing skeleton.
 *
 * Pure presentational: give it the output of computeStructuralMetrics().
 */

const Wrap = styled.div`
  color: #fff;
  .legend {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
    margin-bottom: 14px;
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.7);
    .item { display: flex; align-items: center; gap: 6px; }
    .dot { width: 11px; height: 11px; border-radius: 50%; }
  }
  .hint { font-size: 0.78rem; color: rgba(255,255,255,0.4); margin: 0 0 10px; }
`;

const Plot = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 14px;
  padding: 8px 4px;
  overflow-x: auto;
`;

const W = 1040;
const LANE_H = 92;
const PAD_L = 70;
const PAD_R = 24;

export default function ReachMapView({ result }) {
  const { t } = useTranslation();
  const { lanes, maxInf, scale } = useMemo(() => {
    const skeleton = (result?.nodes || []).filter((n) => n.influence > 0);
    const maxInf = Math.max(1, ...skeleton.map((n) => n.influence));

    // Group by dependence (causal depth) → one lane per depth value present.
    const byDepth = new Map();
    skeleton.forEach((n) => {
      if (!byDepth.has(n.dependence)) byDepth.set(n.dependence, []);
      byDepth.get(n.dependence).push(n);
    });
    const lanes = [...byDepth.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([depth, nodes]) => ({
        depth,
        nodes: nodes.slice().sort((a, b) => b.influence - a.influence),
      }));

    const rMin = 4;
    const rMax = 26;
    const scale = (inf) => rMin + Math.sqrt(inf / maxInf) * (rMax - rMin);
    return { lanes, maxInf, scale };
  }, [result]);

  if (!result) return null;

  const H = Math.max(LANE_H, lanes.length * LANE_H);
  const innerW = W - PAD_L - PAD_R;

  // Label the strongest few per lane (and globally big ones stand out anyway).
  const labelIds = new Set(
    (result.nodes || [])
      .filter((n) => n.influence > 0)
      .slice()
      .sort((a, b) => b.influence - a.influence)
      .slice(0, 10)
      .map((n) => n.id)
  );

  return (
    <Wrap>
      <div className="legend">
        {['driving', 'relay', 'dependent', 'autonomous'].map((q) => (
          <span className="item" key={q}>
            <span className="dot" style={{ background: QUADRANT_META[q].color }} />
            {t(`pivotPoints.quadrant.${q}`)}
          </span>
        ))}
        <span className="item">{t('pivotPoints.reachMap.sizeLegend', { max: maxInf })}</span>
      </div>
      <p className="hint">{t('pivotPoints.reachMap.hint')}</p>
      <Plot>
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', minWidth: 680 }}>
          {lanes.map((lane, li) => {
            const cy = li * LANE_H + LANE_H / 2;
            const n = lane.nodes.length;
            return (
              <g key={lane.depth}>
                <line x1={PAD_L} y1={cy + LANE_H / 2 - 1} x2={W - PAD_R} y2={cy + LANE_H / 2 - 1} stroke="rgba(255,255,255,0.05)" />
                <text x={12} y={cy + 4} fill="rgba(255,255,255,0.5)" fontSize="12">{t('pivotPoints.common.level', { depth: lane.depth })}</text>
                <text x={12} y={cy + 20} fill="rgba(255,255,255,0.3)" fontSize="10">({n})</text>
                {lane.nodes.map((node, i) => {
                  const cx = PAD_L + (n === 1 ? innerW / 2 : (i / (n - 1)) * innerW);
                  const r = scale(node.influence);
                  const showLabel = labelIds.has(node.id);
                  return (
                    <g key={node.id}>
                      <circle
                        cx={cx} cy={cy} r={r}
                        fill={QUADRANT_META[node.quadrant].color}
                        fillOpacity="0.78"
                        stroke="rgba(0,0,0,0.35)" strokeWidth="0.5"
                      >
                        <title>{t('pivotPoints.reachMap.nodeTitle', { label: node.label, influence: node.influence, dependence: node.dependence })}</title>
                      </circle>
                      {showLabel && (
                        <text x={cx} y={cy - r - 4} fill="rgba(255,255,255,0.85)" fontSize="10" textAnchor="middle">
                          {node.label.length > 22 ? node.label.slice(0, 21) + '…' : node.label}
                        </text>
                      )}
                    </g>
                  );
                })}
              </g>
            );
          })}
        </svg>
      </Plot>
    </Wrap>
  );
}
